import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaClipboardCheck, FaTimesCircle, FaPlus, FaTimes } from 'react-icons/fa';

const PHASE_STATUSES = ['Planning', 'Completed', 'Pending'];

export default function ClubEvaluation() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProject, setNewProject] = useState({ project_name: '', category: '', chairperson: '', phases: [] });
  const [phaseInputs, setPhaseInputs] = useState({ name: '', status: 'Planning', date: '' });
  const [addingPhaseTo, setAddingPhaseTo] = useState(null);

  useEffect(() => {
    fetch('https://sliitleo.org/leo-club-backend/portal4/get_evaluation_projects.php')
      .then(res => res.json())
      .then(data => {
        setProjects(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load projects');
        setLoading(false);
      });
  }, []);

  // Add new project
  const handleAddProject = () => {
    const toSend = { ...newProject, phases: [] };
    fetch('https://sliitleo.org/leo-club-backend/portal4/add_project.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toSend)
    })
      .then(res => res.json())
      .then(added => {
        setProjects([added, ...projects]);
        setShowAddModal(false);
        setNewProject({ project_name: '', category: '', chairperson: '', phases: [] });
      });
  };

  // Add phase to a project
  const handleAddPhase = (projectId) => {
    if (!phaseInputs.name) return;
    const updatedProjects = projects.map(proj => {
      if (proj.project_id === projectId) {
        const newPhase = {
          name: phaseInputs.name,
          status: phaseInputs.status,
          date: phaseInputs.date
        };
        const updatedPhases = [...(proj.phases || []), newPhase];
        // Update backend
        fetch('https://sliitleo.org/leo-club-backend/portal4/update_phase_status.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: projectId, phases: updatedPhases })
        });
        return { ...proj, phases: updatedPhases };
      }
      return proj;
    });
    setProjects(updatedProjects);
    setPhaseInputs({ name: '', status: 'Planning', date: '' });
    setAddingPhaseTo(null);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString();
  };
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Visual-only helper: maps a phase status to the shared pill tone
  const phaseTone = (status) =>
    status === 'Completed' ? 'pill-success' : status === 'Planning' ? 'pill-warning' : 'pill-danger';

  return (
    <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-10 relative">
        <div className="page-head">
          <div>
            <h1>Club Evaluation</h1>
            <p className="page-desc">Projects &middot; track evaluation phases and status</p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <FaPlus size={13} /> Add Project
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12" style={{ color: 'var(--text-2)' }}>
              <div className="spinner lg" style={{ marginBottom: 12 }} />
              <span>Loading projects...</span>
            </div>
          ) : error ? (
            <div className="col-span-full error-banner">
              <FaTimesCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          ) : projects.length === 0 ? (
            <div className="col-span-full empty-state">
              <div className="empty-icon"><FaClipboardCheck /></div>
              <h3>No projects found</h3>
              <p>Add a project to start tracking its evaluation phases.</p>
            </div>
          ) : (
            projects.map((proj) => (
              <div
                key={proj.project_id}
                className="panel"
                style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
              >
                <div className="flex items-center gap-3">
                  <span
                    style={{
                      width: 36, height: 36, borderRadius: '50%', background: 'rgba(124,58,237,.16)',
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}
                  >
                    <FaClipboardCheck style={{ color: 'var(--accent)' }} size={15} />
                  </span>
                  <span className="font-bold truncate" style={{ flex: 1, fontSize: 15 }} title={proj.project_name}>
                    {proj.project_name}
                  </span>
                  <span className={`pill ${proj.report_status === 'Completed' ? 'pill-success' : 'pill-warning'}`}>
                    <span className="dot" />
                    {proj.report_status || 'Pending'}
                  </span>
                </div>

                <div className="text-sm" style={{ color: 'var(--text-2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div><span style={{ color: 'var(--text-3)' }}>Category:</span> {proj.category || '-'}</div>
                  <div><span style={{ color: 'var(--text-3)' }}>Officers:</span> {proj.chairperson || '-'}</div>
                  <div><span style={{ color: 'var(--text-3)' }}>Date:</span> {formatDate(proj.date)}</div>
                </div>

                <div>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>Phases</div>
                  {(proj.phases && proj.phases.length > 0) ? (
                    <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', margin: 0, padding: 0 }}>
                      {proj.phases.map((phase, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-2"
                          style={{
                            fontSize: 12, background: 'var(--surface-2)', padding: '8px 10px',
                            borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                          }}
                        >
                          <span className="font-semibold truncate" style={{ flex: 1 }}>{phase.name}</span>
                          <span className={`pill ${phaseTone(phase.status)}`}>
                            <span className="dot" />
                            {phase.status}
                          </span>
                          <span className="num" style={{ color: 'var(--text-3)', fontSize: 11 }}>{formatDate(phase.date)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs" style={{ color: 'var(--text-3)' }}>No phases yet.</div>
                  )}

                  {/* Add Phase Button */}
                  {addingPhaseTo === proj.project_id ? (
                    <div
                      className="mt-3"
                      style={{ background: 'var(--surface-2)', padding: 'var(--sp-3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                    >
                      <div className="field">
                        <label className="field-label" htmlFor={`phase-name-${proj.project_id}`}>Phase name</label>
                        <input
                          id={`phase-name-${proj.project_id}`}
                          placeholder="e.g. Planning"
                          value={phaseInputs.name}
                          onChange={e => setPhaseInputs({ ...phaseInputs, name: e.target.value })}
                        />
                      </div>
                      <div className="field">
                        <label className="field-label" htmlFor={`phase-status-${proj.project_id}`}>Status</label>
                        <select
                          id={`phase-status-${proj.project_id}`}
                          value={phaseInputs.status}
                          onChange={e => setPhaseInputs({ ...phaseInputs, status: e.target.value })}
                        >
                          {PHASE_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                        </select>
                      </div>
                      <div className="field" style={{ marginBottom: 'var(--sp-3)' }}>
                        <label className="field-label" htmlFor={`phase-date-${proj.project_id}`}>Date</label>
                        <input
                          id={`phase-date-${proj.project_id}`}
                          type="date"
                          value={phaseInputs.date}
                          onChange={e => setPhaseInputs({ ...phaseInputs, date: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-primary btn-sm" onClick={() => handleAddPhase(proj.project_id)}>Add Phase</button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setAddingPhaseTo(null); setPhaseInputs({ name: '', status: 'Planning', date: '' }); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className="btn btn-ghost btn-sm mt-3" onClick={() => setAddingPhaseTo(proj.project_id)}>
                      <FaPlus size={11} /> Add Phase
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add Project Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="modal-panel panel" onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FaPlus size={13} /> Add New Project</h3>
                <button className="icon-btn" aria-label="Close" onClick={() => setShowAddModal(false)}>
                  <FaTimes size={14} />
                </button>
              </div>
              <div className="modal-body">
                <div className="field">
                  <label className="field-label" htmlFor="new-project-name">Project name</label>
                  <input
                    id="new-project-name"
                    placeholder="Project Name"
                    value={newProject.project_name}
                    onChange={e => setNewProject({ ...newProject, project_name: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="new-project-category">Category</label>
                  <input
                    id="new-project-category"
                    placeholder="Category"
                    value={newProject.category}
                    onChange={e => setNewProject({ ...newProject, category: e.target.value })}
                  />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="new-project-chair">Chairperson</label>
                  <input
                    id="new-project-chair"
                    placeholder="Chairperson"
                    value={newProject.chairperson}
                    onChange={e => setNewProject({ ...newProject, chairperson: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-foot">
                <button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleAddProject}>Add Project</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
